import { getCookieParam } from '~/utils/cookies';

import {
  ACCESS_TOKEN_COOKIE_NAME,
  ACCESS_TOKEN_COOKIE_ORIGIN_PARAM,
  FROM_IN_APP_BROWSER,
} from '../constants/auth';

import type { Request } from 'express';

/**
 * Format international phone number to local format
 * @param phoneNumber +614xxxxxxx
 * @param mask xxxx xxx xxx
 * @returns 04xxxxxxxx
 */
export const formatInternationalPhoneNumberTolocal = (
  phoneNumber?: string,
  mask?: string
): string => {
  if (!phoneNumber) {
    return '';
  }

  // Replace +61 with 0 if applicable
  let formatted = phoneNumber.startsWith('+61') ? phoneNumber.replace('+61', '0') : phoneNumber;
  // Remove non-digit characters
  formatted = formatted.replace(/\D/g, '');

  // Apply mask if specified
  if (mask === 'xxxx xxx xxx') {
    const match = formatted.match(/^(\d{0,4})(\d{0,3})(\d{0,3})$/);
    if (match) {
      formatted = [match[1], match[2], match[3]].filter(Boolean).join(' ');
    }
  }

  return formatted;
};

/**
 * Format local phone number to international format
 * @param phoneNumber 04xx xxx xxx
 * @returns +614xxxxxxx
 */
export const formatLocalPhoneNumberToInternational = (phoneNumber: string): string => {
  if (!phoneNumber.startsWith('0')) {
    return phoneNumber;
  }

  return phoneNumber.replace(/^0/, '+61').replace(/\s/g, '');
};

/**
 * This function is used to get the page url by replacing the params in the slug
 * @param slug "/test/[test]/test"
 * @param params { test: '123' }
 * @returns "/test/123/test"
 */
export const formatPageUrl = (slug: string, params: Record<string, string>): string => {
  let url = slug;

  Object.entries(params).forEach(([key, value]) => {
    url = url.replace(`[${key}]`, value);
  });

  return url;
};

/**
 * Check if the URL path matches the slug pattern
 * @param url The URL to check
 * @param slug The slug pattern to match against
 * @returns true if the URL path matches the slug pattern, false otherwise
 */
export const isPathMatchesSlug = (url: string, slug: string): boolean => {
  try {
    const cleanUrl = new URL(url).pathname; // Strip domain and query
    const urlParts = cleanUrl.split('/').filter(Boolean);
    const slugParts = slug.split('/').filter(Boolean);

    if (urlParts.length !== slugParts.length) {
      return false;
    }

    return slugParts.every((slugPart, index) => {
      const urlPart = urlParts[index];

      // Dynamic part: [param]
      if (slugPart.startsWith('[') && slugPart.endsWith(']')) {
        return true;
      }

      return slugPart === urlPart;
    });
  } catch (error) {
    return false;
  }
};

/**
 * Extracts the query string from the request URL.
 * @param req - Incoming HTTP request from context.req
 * @returns
 */
export const extractSSRQueryString = (req: Request): string => {
  const url = req.url || '';
  const queryString = url.split('?')[1] || '';

  return queryString;
};

export const extractClientQueryString = (): string => {
  if (typeof window !== 'undefined') {
    return window.location.search.substring(1); // removes the "?" at start
  }

  return '';
};

export const isNumberEven = (num: number): boolean => num % 2 === 0;

export const isAppOpenedInNativeAppBrowser = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const accessTokenOrigin = getCookieParam(
    ACCESS_TOKEN_COOKIE_NAME,
    ACCESS_TOKEN_COOKIE_ORIGIN_PARAM
  );

  return accessTokenOrigin === FROM_IN_APP_BROWSER;
};
